import { useApp } from '@libs/core';
import { AssetsSvgMap, ECalculatorErrorType, TCalculatorError, TCommonDataObj, TViewCommonOption } from '@libs/types';
import { CoreInputViewNew, CoreLayoutView, CoreTextView, ORIENTATION_ENUM } from '@core/widgets';
import React, { useState } from 'react';
import { TextStyle } from '../../../types/styles';
import { ButtonPillView } from '../../Buttons/ButtonPillView';
import { ButtonRowView } from '../../Buttons/ButtonRowView';
import { VisualView } from '../../Image/VisualView';
import { PopupComponent } from '../../UI/Popup/PopupComponent';
import { ActionNameEnum, InputEnum, TServerCalculationFunc } from '../utils/types';
import { CodeTimerScreen } from './CodeTimerScreen';

type TPromoCode = {
    text?: string;
    textLock?: string;
    textAddedCode?: string;
    label?: string;
    labelGotIt?: string;
    isFrom: boolean;
    onServerCalculation: TServerCalculationFunc;
    parentStyleNames?: TViewCommonOption['parentStyleNames'];
};

const CodeInput = ({ text, label, isFrom, onServerCalculation, textLock, labelGotIt, textAddedCode, parentStyleNames }: TPromoCode) => {
    const { setPopup, setModal } = useApp();
    const [value, setValue] = useState('');
    const [isFocused, setIsFocused] = useState<boolean>(true);
    const [currentError, setCurrentError] = useState<TCalculatorError | undefined>(undefined);
    const [isFilled, setIsFilled] = useState<boolean>(!!value);
    const isEnabled = Boolean(value && !currentError);
    const lockError = currentError?.type === ECalculatorErrorType.INVALID_CODE_LOCK;
    const errorMessage = currentError?.shortText;

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e?.target?.value;
        setIsFocused(false);
        setIsFilled(!!value);
    };

    const handleFocus = () => {
        setIsFilled(true);
        setIsFocused(true);
        setCurrentError(undefined);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (currentError?.shortText && !lockError) {
            setCurrentError(undefined);
        }
        setValue(e.target.value);
    };
    const handleClick = async () => {
        if (value) {
            const result = await onServerCalculation(value, 'code', isFrom, ActionNameEnum.onAddCode);
            if (result?.errors?.code) {
                setCurrentError(result?.errors?.code);
            } else if (result?.value && typeof result.value !== 'string' && (result.value as TCommonDataObj)?.code) {
                setPopup(null);
                setModal(<CodeTimerScreen text={textAddedCode} />);
            } else {
                setPopup(null);
            }
        }
    };

    const handleErrorClick = () => {
        setPopup(
            <PopupComponent
                header={
                    <CoreTextView
                        text={textLock ?? ''}
                        styleName="calculatorBsText"
                        parentStyleNames={parentStyleNames}
                        styleProps={{ ...TextStyle.main, main: 'var(--color-black)', textAlign: 'center' as const }}
                    />
                }
                externalContent={[
                    <ButtonRowView key="got it" label={labelGotIt} iconName={'ic_check'} parentStyleNames={parentStyleNames} onClick={() => setPopup(null)} />,
                ]}
            />,
        );
    };
    return (
        <>
            <CoreTextView
                text={text ?? ''}
                styleName="calculatorBsText"
                parentStyleNames={parentStyleNames}
                styleProps={{ ...TextStyle.main, main: 'var(--color-black)', textAlign: 'center' as const }}
            />
            <CoreInputViewNew
                value={value}
                name={InputEnum.CODE}
                type="text"
                autoFocus
                isFocused={isFocused}
                isFilled={isFilled}
                isError={!!errorMessage}
                styleName="calculatorPromoCodeInput"
                parentStyleNames={parentStyleNames}
                styleProps={{
                    font: TextStyle.main.font,
                    fontStyle: { ...TextStyle.main.fontStyle },
                    textAlign: 'center',
                    fillContainer: true,
                    main: 'var(--color-purple_100)',
                    outline: 'var(--color-grey_16_lc)',
                    onSurface: 'var(--color-black)',
                    error: { outline: 'var(--color-pink_100)', onSurfaceVariant: 'var(--color-pink_100)' },
                    margin: { top: 48, bottom: 48 },
                }}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
            >
                {errorMessage && (
                    <CoreLayoutView
                        styleProps={{
                            orientation: ORIENTATION_ENUM.HORIZONTAL,
                            lineX: 'center',
                            lineY: 'center',
                            gap: 2,
                            padding: { top: 8, left: 16, right: 16 },
                        }}
                    >
                        <CoreTextView
                            as="span"
                            text={errorMessage}
                            isError
                            styleName="calculatorPromoCodeInputHelper"
                            parentStyleNames={parentStyleNames}
                            styleProps={{ ...TextStyle.secondary, main: 'var(--color-grey_72_lc)', error: { main: 'var(--color-pink_100)' } }}
                        />
                        {lockError && (
                            <VisualView
                                as="button"
                                uid="calculator_promo_code_helper_icon"
                                iconName={AssetsSvgMap.ic_info_circle_small}
                                defaultIconName={AssetsSvgMap.ic_x}
                                iconVariant="simple"
                                iconHierarchy="destructive"
                                iconWidth={16}
                                iconHeight={16}
                                hovered={{ backgroundColor: 'var(--color-pink_16_lc)' }}
                                parentStyleNames={parentStyleNames}
                                styleName="calculatorPromoCodeInputHelperIcon"
                                handleClick={handleErrorClick}
                                margin={{ left: 4 }}
                            />
                        )}
                    </CoreLayoutView>
                )}
            </CoreInputViewNew>
            <ButtonPillView
                hierarchy="primary"
                position="floating"
                label={label ?? ''}
                parentStyleNames={parentStyleNames}
                onClick={handleClick}
                isDisabled={!isEnabled}
            />
        </>
    );
};
export { CodeInput };
